import { useMemo } from "react";
import { DateTime } from "luxon";
import { CheckCircle2, Circle, MessageSquareWarning, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useAudio } from "@/contexts/audio-context";
import { CallAnalyticsType, PlaybookAnalysisStatus, PlaybookCriterionVerdict, PlaybookMilestoneVerdict } from "@/types/voice/call_analytics";

const STATUS_CONFIG: Record<string, { label: string; variant: "green" | "amber" | "blue" | "red" | "gray" }> = {
    completed: { label: "Completed", variant: "green" },
    partial: { label: "Partial (criteria missing)", variant: "amber" },
    queued: { label: "Queued", variant: "blue" },
    processing: { label: "Processing", variant: "blue" },
    failed: { label: "Failed", variant: "red" },
    no_playbook: { label: "No playbook", variant: "gray" }
};

function formatMs(ms?: number | null): string {
    if (ms === null || ms === undefined) return "";
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function TimestampLink({ ms }: { ms?: number | null }) {
    const { setCurrentTime } = useAudio();
    if (ms === null || ms === undefined) return null;
    return (
        <button type="button" className="font-mono text-xs text-blue-600 hover:underline" onClick={() => setCurrentTime(ms / 1000)} title="Jump to this moment">
            {formatMs(ms)}
        </button>
    );
}

function ScoreCard({ title, value }: { title: string; value?: number | null }) {
    const hasValue = value !== null && value !== undefined;
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
            </CardHeader>
            <CardContent>
                {hasValue ? (
                    <div className="space-y-2">
                        <div className="text-2xl font-bold">{Math.round(value!)}</div>
                        <Progress value={value!} />
                    </div>
                ) : (
                    <div className="text-sm text-muted-foreground">N/A</div>
                )}
            </CardContent>
        </Card>
    );
}

function CriterionLevelBadge({ criterion }: { criterion: PlaybookCriterionVerdict }) {
    if (criterion.status === "not_applicable") {
        return <Badge variant="gray">N/A</Badge>;
    }
    if (criterion.status === "error" || criterion.level === null || criterion.level === undefined) {
        return <Badge variant="outline">—</Badge>;
    }
    const variant = criterion.level >= 4 ? "green" : criterion.level === 3 ? "teal" : criterion.level === 2 ? "amber" : "red";
    return <Badge variant={variant}>{criterion.level} / 4</Badge>;
}

export function PlaybookAnalytics({ analytics }: { analytics: CallAnalyticsType }) {
    const status = (analytics.playbook_status || "") as PlaybookAnalysisStatus;
    const analysis = analytics.playbook_analysis;

    const milestonesByPhase = useMemo(() => {
        const grouped = new Map<string, Array<[string, PlaybookMilestoneVerdict]>>();
        Object.entries(analysis?.milestones || {}).forEach(([code, verdict]) => {
            const phase = verdict.phase_code || "";
            if (!grouped.has(phase)) grouped.set(phase, []);
            grouped.get(phase)!.push([code, verdict]);
        });
        return grouped;
    }, [analysis]);

    if (!status) {
        return <div className="mt-4 text-sm text-muted-foreground">No playbook analysis requested for this call.</div>;
    }

    const statusConfig = STATUS_CONFIG[status] || { label: status, variant: "gray" as const };
    const phaseSegments = analysis?.phase_segments || [];
    const objections = analysis?.objections || [];
    const criteria = analysis?.criteria || [];

    return (
        <div className="mt-4 space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center gap-3">
                <Sparkles className="size-5 text-purple-500" />
                <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                {analytics.playbook_code && (
                    <span className="font-mono text-sm text-muted-foreground">
                        {analytics.playbook_code}
                        {analytics.playbook_version ? `@${analytics.playbook_version}` : ""}
                    </span>
                )}
                {analytics.playbook_updated_at && (
                    <span className="text-xs text-muted-foreground">Updated {DateTime.fromISO(analytics.playbook_updated_at).toLocaleString(DateTime.DATETIME_SHORT)}</span>
                )}
            </div>

            {analytics.playbook_error && status === "failed" && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <MessageSquareWarning className="mt-0.5 size-4 shrink-0" />
                    <span>{analytics.playbook_error}</span>
                </div>
            )}

            {(status === "queued" || status === "processing") && <div className="text-sm text-muted-foreground">The playbook analysis is in progress…</div>}

            {analysis && (
                <>
                    {/* Macro scores */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <ScoreCard title="Overall score" value={analytics.overall_score} />
                        <ScoreCard title="Milestone completion" value={analytics.milestone_completion_rate} />
                        <ScoreCard title="Objection handling" value={analytics.objection_handling_score} />
                    </div>

                    {/* Summary */}
                    {analysis.summary && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Summary</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="whitespace-pre-wrap text-sm">{analysis.summary}</p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Phases & milestones */}
                    {phaseSegments.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Phases & milestones</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {phaseSegments.map((segment, index) => (
                                    <div key={`${segment.phase_code}-${index}`} className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-sm font-semibold">{segment.phase_code}</span>
                                            <span className="text-xs text-muted-foreground">
                                                <TimestampLink ms={segment.start_ms} /> → <TimestampLink ms={segment.end_ms} />
                                            </span>
                                        </div>
                                        <div className="space-y-1 pl-4">
                                            {(milestonesByPhase.get(segment.phase_code) || []).map(([code, verdict]) => (
                                                <div key={code} className="flex items-start gap-2 text-sm">
                                                    {verdict.validated ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-green-600" /> : <Circle className="mt-0.5 size-4 shrink-0 text-gray-300" />}
                                                    <div className="min-w-0">
                                                        <span className="font-mono text-xs">{code}</span>
                                                        {verdict.evidence && (
                                                            <p className="text-xs text-muted-foreground">
                                                                “{verdict.evidence}” <TimestampLink ms={verdict.evidence_at_ms} />
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Objections */}
                    {objections.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Objections</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {objections.map((objection, index) => (
                                    <div key={`${objection.signal_code}-${index}`} className="rounded-md border p-3 text-sm">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-mono text-xs font-semibold">{objection.signal_code}</span>
                                            <TimestampLink ms={objection.at_ms} />
                                            {objection.handled_well !== null && objection.handled_well !== undefined && (
                                                <Badge variant={objection.handled_well ? "green" : "red"}>{objection.handled_well ? "Handled well" : "Poorly handled"}</Badge>
                                            )}
                                        </div>
                                        {objection.prospect_quote && <p className="mt-1 text-xs italic text-muted-foreground">“{objection.prospect_quote}”</p>}
                                        {objection.agent_response_summary && <p className="mt-1 text-xs">{objection.agent_response_summary}</p>}
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Criteria */}
                    {criteria.length > 0 && (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Evaluation criteria</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {criteria.map(criterion => (
                                    <div key={criterion.code} className="rounded-md border p-3 text-sm">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <CriterionLevelBadge criterion={criterion} />
                                            <span className="font-medium">{criterion.label || criterion.code}</span>
                                            {criterion.status === "not_applicable" && criterion.na_reason && <span className="text-xs text-muted-foreground">({criterion.na_reason})</span>}
                                        </div>
                                        {criterion.rationale && <p className="mt-1 text-xs text-muted-foreground">{criterion.rationale}</p>}
                                        {criterion.evidence && <p className="mt-1 text-xs italic text-muted-foreground">“{criterion.evidence}”</p>}
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}
